import React, { useState, useRef, useEffect } from 'react';
import { useAuth, API } from '../App';
import { DashboardLayout } from '../components/DashboardLayout';
import PrerequisiteGate from '../components/PrerequisiteGate';
import AutofillBanner, { AutofillBadge } from '../components/AutofillBanner';
import { useUserContext } from '../hooks/useUserContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { 
  Send, 
  Sparkles,
  Loader2,
  Bot,
  Lightbulb,
  Target,
  Palette,
  FileText,
  TrendingUp,
  Users,
  Trash2
} from 'lucide-react';
import VoiceInput from '../components/VoiceInput';

const AIAssistantContent = () => {
  const { token, user } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [userMessage, setUserMessage] = useState('');
  const [conversation, setConversation] = useState([]);
  const messagesEndRef = useRef(null);
  
  // Auto-fill context
  const { 
    hasData: hasContextData, 
    sources, 
    sourcesDisplay, 
    summaryMessage 
  } = useUserContext();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [conversation]);

  useEffect(() => {
    // Welcome message - personalized based on available context
    if (conversation.length === 0) {
      const userName = user?.name?.split(' ')[0] || 'visionnaire';
      let welcomeMsg = t('aiAssistant.welcomeMessage', { name: userName });
      
      // Add premium context-aware intro if user has data
      if (hasContextData && sources.length > 0) {
        const hasVision = sources.includes('vision');
        const hasProfile = sources.includes('profile');
        const hasAudit = sources.includes('audit');
        
        let contextIntro = '\n\n';
        if (hasVision && hasProfile) {
          contextIntro += `J'ai analysé ta vision et ton profil — je connais ton univers de marque et je suis prête à t'accompagner de façon personnalisée.`;
        } else if (hasVision) {
          contextIntro += `J'ai accès à ta vision de marque — chaque conseil sera aligné avec ton identité.`;
        } else if (hasProfile) {
          contextIntro += `Ton profil d'entrepreneure a été intégré — mes recommandations seront adaptées à toi.`;
        } else {
          contextIntro += `J'ai accès à ton parcours — je peux t'aider de façon personnalisée.`;
        }
        
        welcomeMsg += contextIntro;
      }
      
      setConversation([{
        role: 'assistant',
        content: welcomeMsg
      }]);
    }
  }, [user?.name, t, hasContextData]);

  const topicCards = [
    {
      icon: Target,
      title: t('aiAssistant.topics.brandStrategy.title'),
      description: t('aiAssistant.topics.brandStrategy.description'),
      prompt: t('aiAssistant.topics.brandStrategy.prompt')
    },
    {
      icon: Palette,
      title: t('aiAssistant.topics.visualIdentity.title'),
      description: t('aiAssistant.topics.visualIdentity.description'),
      prompt: t('aiAssistant.topics.visualIdentity.prompt')
    },
    {
      icon: FileText,
      title: t('aiAssistant.topics.contentIdeas.title'),
      description: t('aiAssistant.topics.contentIdeas.description'),
      prompt: t('aiAssistant.topics.contentIdeas.prompt')
    },
    {
      icon: TrendingUp,
      title: t('aiAssistant.topics.growthStrategy.title'),
      description: t('aiAssistant.topics.growthStrategy.description'),
      prompt: t('aiAssistant.topics.growthStrategy.prompt')
    },
    {
      icon: Users,
      title: t('aiAssistant.topics.audienceConnection.title'),
      description: t('aiAssistant.topics.audienceConnection.description'),
      prompt: t('aiAssistant.topics.audienceConnection.prompt')
    },
    {
      icon: Lightbulb,
      title: t('aiAssistant.topics.brandReview.title'),
      description: t('aiAssistant.topics.brandReview.description'),
      prompt: t('aiAssistant.topics.brandReview.prompt')
    }
  ];

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!userMessage.trim() || loading) return;

    const newUserMessage = { role: 'user', content: userMessage };
    setConversation(prev => [...prev, newUserMessage]);
    setLoading(true);
    setUserMessage('');

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/ai/assistant`, {
        message: userMessage,
        conversation_history: conversation
      }, { headers });

      setConversation(prev => [...prev, {
        role: 'assistant',
        content: response.data.response
      }]);
    } catch (error) {
      toast.error(t('errors.generic'));
      setConversation(prev => [...prev, {
        role: 'assistant',
        content: t('aiAssistant.connectionError')
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleTopicSelect = (prompt) => {
    setUserMessage(prompt);
  };

  const clearConversation = () => {
    const userName = user?.name?.split(' ')[0] || 'visionnaire';
    setConversation([{
      role: 'assistant',
      content: t('aiAssistant.startFresh', { name: userName })
    }]);
  };

  return (
    <DashboardLayout title={t('aiAssistant.title')}>
      <div className="max-w-6xl mx-auto">
        {/* Auto-fill Badge for AI Assistant */}
        {hasContextData && conversation.length <= 1 && (
          <div className="mb-4 flex justify-center">
            <AutofillBadge hasData={hasContextData} sources={sources} />
          </div>
        )}
        
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Chat Area */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="card-premium h-[calc(100vh-200px)] flex flex-col"
            >
              {/* Chat Header */}
              <div className="p-4 border-b border-[#1C1C1C] flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#D4AF37]/20 to-[#E5C585]/10 flex items-center justify-center">
                    <Bot className="text-[#D4AF37]" size={24} />
                  </div>
                  <div>
                    <h2 className="font-serif text-xl text-white">Vision Lab AI</h2>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                      <span className="text-[#52525B] text-sm">{t('aiAssistant.subtitle')}</span>
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  onClick={clearConversation}
                  className="text-[#52525B] hover:text-white"
                  data-testid="clear-conversation"
                >
                  <Trash2 size={18} />
                </Button>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {conversation.map((msg, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[80%] ${
                      msg.role === 'user' 
                        ? 'bg-[#D4AF37]/10 border border-[#D4AF37]/20 p-4' 
                        : 'bg-[#1C1C1C]/50 p-5'
                    }`}>
                      {msg.role === 'assistant' && (
                        <div className="flex items-center gap-2 mb-3">
                          <Sparkles size={14} className="text-[#D4AF37]" />
                          <span className="text-[#D4AF37] text-xs font-accent tracking-wider">VISION LAB AI</span>
                        </div>
                      )}
                      <p className="text-[#E4E4E7] whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                    </div>
                  </motion.div>
                ))}

                {loading && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex justify-start"
                  >
                    <div className="bg-[#1C1C1C]/50 p-5 flex items-center gap-3">
                      <Loader2 size={18} className="animate-spin text-[#D4AF37]" />
                      <span className="text-[#A1A1AA]">{t('aiAssistant.thinking')}</span>
                    </div>
                  </motion.div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <div className="p-4 border-t border-[#1C1C1C]">
                <form onSubmit={handleSubmit} className="flex gap-3">
                  <div className="relative flex-1">
                    <Textarea
                      value={userMessage}
                      onChange={(e) => setUserMessage(e.target.value)}
                      placeholder={t('aiAssistant.placeholder')}
                      className="w-full bg-transparent border border-[#27272A] focus:border-[#D4AF37] rounded-none p-4 pr-14 min-h-[60px] max-h-[120px] text-white placeholder:text-[#52525B] resize-none"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSubmit();
                        }
                      }}
                      data-testid="assistant-page-input"
                    />
                    <div className="absolute right-3 bottom-3">
                      <VoiceInput 
                        onTranscription={(text) => {
                          setUserMessage(prev => prev ? `${prev} ${text}` : text);
                        }}
                      />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    disabled={loading || !userMessage.trim()}
                    className="btn-gold px-6 self-end"
                    data-testid="assistant-page-submit"
                  >
                    {loading ? <Loader2 size={20} className="animate-spin" /> : <Send size={20} />}
                  </Button>
                </form>
              </div>
            </motion.div>
          </div>

          {/* Topic Cards Sidebar */}
          <div className="space-y-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <p className="text-[#A1A1AA] text-xs font-accent tracking-wider mb-4">{t('aiAssistant.exploreTopics')}</p>
              <div className="space-y-3">
                {topicCards.map((topic, i) => (
                  <motion.button
                    key={i}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 + i * 0.05 }}
                    onClick={() => handleTopicSelect(topic.prompt)}
                    className="w-full card-premium p-4 text-left hover:border-[#D4AF37]/50 transition-all group"
                    data-testid={`topic-${i}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-[#1C1C1C] flex items-center justify-center group-hover:bg-[#D4AF37]/10 transition-colors">
                        <topic.icon size={18} className="text-[#D4AF37]" />
                      </div>
                      <div>
                        <h4 className="text-white font-medium text-sm mb-1">{topic.title}</h4>
                        <p className="text-[#52525B] text-xs">{topic.description}</p>
                      </div>
                    </div>
                  </motion.button>
                ))}
              </div>
            </motion.div>

            {/* Tips Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="card-premium p-5 bg-gradient-to-br from-[#D4AF37]/5 to-transparent"
            >
              <Lightbulb size={20} className="text-[#D4AF37] mb-3" />
              <h4 className="text-white font-serif mb-2">{t('aiAssistant.proTip.title')}</h4>
              <p className="text-[#A1A1AA] text-sm leading-relaxed">
                {t('aiAssistant.proTip.content')}
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

// Wrap with PrerequisiteGate
const AIAssistant = () => {
  return (
    <PrerequisiteGate 
      toolId="ai-assistant" 
      toolTitle="Assistant IA"
    >
      <AIAssistantContent />
    </PrerequisiteGate>
  );
};

export default AIAssistant;
